import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps } from '../../utils/buildingSelection';
import {
  apartmentsApi,
  type ApartmentUsageType,
  type Building,
  type CreateApartmentUsageTypeData,
} from '../../api/apartmentsApi';

interface AddApartmentUsageTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedBuildingId?: string;
  usageType?: ApartmentUsageType | null;
}

const AddApartmentUsageTypeModal: React.FC<AddApartmentUsageTypeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedBuildingId,
  usageType,
}) => {
  const { t } = useLang();
  const { user } = useAuth();
  const isEditMode = Boolean(usageType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);

  const [formData, setFormData] = useState<CreateApartmentUsageTypeData>({
    name: '',
    code: '',
    description: '',
    managementFeeCode: '',
    isDefault: false,
    buildingId: selectedBuildingId || '',
  });

  useEffect(() => {
    if (isOpen) {
      loadBuildings();
      if (usageType) {
        setFormData({
          name: usageType.name,
          code: usageType.code,
          description: usageType.description || '',
          managementFeeCode: usageType.managementFeeCode || '',
          isDefault: usageType.isDefault,
          buildingId: usageType.buildingId,
        });
      } else {
        setFormData({
          name: '',
          code: '',
          description: '',
          managementFeeCode: '',
          isDefault: false,
          buildingId: selectedBuildingId || '',
        });
      }
      setError(null);
    }
  }, [isOpen, selectedBuildingId, usageType]);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      if (!usageType) {
        setFormData((prev) => ({
          ...prev,
          buildingId: applyDefaultBuildingSelection(data, user, prev.buildingId || selectedBuildingId || ''),
        }));
      }
    } catch (err) {
      console.error('Failed to load buildings:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError(t('pleaseEnterUsageTypeName'));
      return;
    }
    if (!formData.code.trim()) {
      setError(t('pleaseEnterUsageTypeCode'));
      return;
    }
    if (!formData.buildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setLoading(true);
      const managementFeeCode = formData.managementFeeCode?.trim().toUpperCase() || null;
      const payload = {
        ...formData,
        code: formData.code.trim().toUpperCase(),
        managementFeeCode,
      };

      if (isEditMode && usageType) {
        await apartmentsApi.updateApartmentUsageType({
          id: usageType.id,
          name: payload.name,
          code: payload.code,
          description: payload.description,
          managementFeeCode: payload.managementFeeCode,
          isDefault: payload.isDefault,
        });
      } else {
        await apartmentsApi.createApartmentUsageType(payload);
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(isEditMode ? t('errorUpdatingUsageType') : t('errorCreatingUsageType'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-red-600">
            {isEditMode ? t('editApartmentUsageType') : t('addApartmentUsageType')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {shouldShowBuildingSelector(buildings) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('selectBuilding')}</label>
            <select
              name="buildingId"
              value={formData.buildingId}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-700"
              required
              disabled={isEditMode || !needsBuildingSelection(buildings)}
            >
              <option value="">{t('selectBuildingPlaceholder')}</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>{building.name}</option>
              ))}
            </select>
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('name')}</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('usageTypeNamePlaceholder')}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('code')}</label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="CAN_HO, TM"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('description')}</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('usageTypeManagementFeeCode')}</label>
            <input
              type="text"
              name="managementFeeCode"
              value={formData.managementFeeCode || ''}
              onChange={handleChange}
              placeholder="DV001, DV008, …"
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">{t('usageTypeManagementFeeCodeHint')}</p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              name="isDefault"
              checked={formData.isDefault}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">{t('setAsDefaultUsageType')}</label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50">
              {t('close')}
            </button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50">
              {loading ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddApartmentUsageTypeModal;
